import useOptions from "features/masterData/hooks/useOptions";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { generateRandomString } from "utils/helper";
import * as services from "../services/reportApi";
import { setIsLoading, setReportTableList } from "../slices/reportSlice";

function useEmployeeHistoryGroup() {
    const { t, i18n } = useTranslation();
    const { employeeTypeOptions, employmentTypeOptions, getEmployeeTypeOptions, getEmploymentTypeOptions } = useOptions();
    const { employeeStatusTypeOptions, getEmployeeStstuaTypeOptions } = useOptions();
    const { informationTypeOptions, getInformationTypeOptions } = useOptions();
    // const { getDepartment = () => { } } = useOptions();
    // const department = useSelector((state) => state.options.department);

    const dispatch = useDispatch();
    const currentCompany = useSelector((state) => state.company.currentCompany);
    const currentEmployeeId = useSelector((state) => state.company.currentEmployee);
    const reportTableList = useSelector((state) => state.report.reportTableList || []);
    const [showReport, setShowReport] = useState(false);
    const isLoading = useSelector((state) => state.employee.isLoading);

    const [positionOptions, setPositionOptions] = useState([]);
    const [departmentListOption, setDepartmentListOption] = useState([]);
    const [pagination, setPagination] = useState({ page: 0, currentPage: 0, size: 10 });
    const [search, setSearch] = useState({});
    const [selectedDates, setSelectedDates] = useState(null);
    const [isDownloading, setIsDownloading] = useState(false);

    const [isModalOpen, setIsModalOpen] = useState(false);
    const showModal = () => {
        setIsModalOpen(true);
    };

    const handleDateChange = (dates) => {
        // console.log("Selected Dates:", dates);
        setSelectedDates(dates);
    };

    function toParams(params = {}) {
        const { pagination = {}, sortBy = [] } = params;
        const modifiedParams = {
            pagination: {
                page: pagination?.page,
                size: pagination?.pageSize,
            },
            sortBy: sortBy
                .filter((s) => s.order !== undefined)
                .map((s) => ({
                    direction: s.order === "descend" ? "desc" : "asc",
                    property: s.field,
                })),
        };
        return modifiedParams;
    }
    const currentEmployee = useSelector((state) => state.company.currentEmployee);

    const modifiedPositionOptions = useMemo(() => {
        const modifiedOptions = positionOptions?.map((opt) => ({
            ...opt,
            id: opt?.positionId,
            value: opt?.positionId,
            nameEn: opt?.positionNameEn,
            name: opt?.positionName,
            label: i18n.language === "en" ? opt?.positionNameEn : opt?.positionName,
        }));
        return modifiedOptions?.sort((a, b) =>
            a?.label?.localeCompare(b?.label, i18n?.language === "en" ? "en" : "th", { sensitivity: "base" })
        );
    }, [i18n.language, positionOptions]);

    const modifiedDepartmentOption = useMemo(() => {
        const modifiedOptions = departmentListOption?.map((opt) => ({
            ...opt,
            value: opt?.id,
            label: i18n.language === "en" ? opt?.nameEn : opt?.name,
        }));

        return modifiedOptions?.sort((a, b) =>
            a?.label?.localeCompare(b?.label, i18n?.language === "en" ? "en" : "th", { sensitivity: "base" })
        );
    }, [i18n.language, departmentListOption]);

    const getPosition = useCallback(async () => {
        if (typeof currentCompany !== "object") {
            try {
                dispatch(setIsLoading(true));
                const values = {
                    requestId: generateRandomString(),
                    companyId: currentCompany,
                    employeeId: currentEmployeeId
                };
                const response = await services.getPosition(values);
                setPositionOptions(response?.data);
            } catch (error) {
                // alert({ type: "error", resultObject: error });
            } finally {
                dispatch(setIsLoading(false));
            }
        }
    }, [currentCompany, currentEmployeeId, dispatch]);


    const getDepartment = useCallback(async () => {
        if (!currentCompany) return null;
        try {
            dispatch(setIsLoading(true));
            const response = await services.getDepartmentOptions({
                requestId: generateRandomString(),
                companyId: currentCompany,
                employeeId: currentEmployeeId
            });

            dispatch(setDepartmentListOption(response?.data));
        } catch (error) {
            // alert({ type: "error", resultObject: error });
        } finally {
            dispatch(setIsLoading(false));
        }
    }, [currentCompany, currentEmployeeId, dispatch]);

    useEffect(() => {
        if (currentCompany) {
            getEmployeeTypeOptions({ companyId: currentCompany, groupType: "EMPLOYEE_TYPE" });
            getEmployeeStstuaTypeOptions();
            getInformationTypeOptions();
            getEmploymentTypeOptions({ companyId: currentCompany });
            getDepartment();
            getPosition();
        }
    }, [currentCompany, getEmployeeTypeOptions, getEmployeeStstuaTypeOptions, getEmploymentTypeOptions, getEmployeeTypeOptions, getPosition]);

    const getReportHistory = useCallback(
        async (params = {}) => {
            try {
                dispatch(setIsLoading(true));
                const response = await services.getReportTableHistory({
                    requestId: generateRandomString(),
                    employeeId: currentEmployee,
                    companyId: currentCompany,
                    ...params,
                    informationType: 1,
                    ...params.pagination,
                });

                dispatch(setReportTableList(response?.data?.page?.content));

                setPagination({
                    ...pagination,
                    // ...params?.request,
                    current: response?.data?.page?.pageable?.pageNumber + 1,
                    pageNo: response?.data?.page?.pageable?.pageNumber + 1,
                    size: response?.data?.page?.pageable?.pageSize,
                    total: response?.data?.page?.totalElements,
                });
            } catch (error) {
                console.error("Error fetching report history:", error);
            } finally {
                dispatch(setIsLoading(false));
            }
        },
        [currentCompany, dispatch, pagination]
    );


    const onChange = useCallback(
        (tablePagination, tableFilters, tableSorter, tableExtra) => {

            const modifiedSorter = Array.isArray(tableSorter)
                ? [...tableSorter]
                : [tableSorter];
            const modifiedParams = toParams({
                pagination: {
                    page: tablePagination.current - 1,
                    pageSize: tablePagination.pageSize,
                    current: tablePagination.current - 1,
                },
                sortBy: modifiedSorter,
            });
            setPagination({ ...pagination, ...modifiedParams?.pagination })
            getReportHistory({ ...modifiedParams, request: pagination.request, ...search });
        },
        [getReportHistory, pagination, search]
    );

    const exportEmployeeRegistrationReport = useCallback(
        async (params = {}) => {
            setIsDownloading(true);
            try {
                dispatch(setIsLoading(true));
                const res = await services.ExportEmployeeRegistrationReportPaging({
                    ...params,
                    companyId: currentCompany,
                    informationType: 1,
                    ...params.pagination,
                    ...search,
                    ...pagination,
                    isReport: "Y"
                });

                const url = window.URL.createObjectURL(res?.data);
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', "employee-registation-report.xlsx");
                document.body.appendChild(link);
                link.click();
                link.remove();

            } catch (error) {
                console.error("Error fetching report history:", error);
            } finally {
                setIsDownloading(false);
                dispatch(setIsLoading(false));
            }
        },
        [currentCompany, dispatch, search, pagination]
    );

    const handleSearch = (values) => {
        setShowReport(true)
        setSearch({})
        // setSearch({ ...values })

        // const startDate = selectedDates?.[0]?.format?.("YYYY-MM-DD") || null;
        // const endDate = selectedDates?.[1]?.format?.("YYYY-MM-DD") || null;

        // const { startToEndDate, ...restValues } = values;

        // getReportHistory({
        //     ...restValues,
        //     ...pagination,
        //     year: values?.year?.format("YYYY"),
        //     startDate,
        //     endDate
        // })

        try {
            setSearch({
                ...values,
                startDate: values?.startToEndDate?.[0] ? values?.startToEndDate?.[0]?.format('YYYY-MM-DD') : null,
                endDate: values?.startToEndDate?.[1] ? values?.startToEndDate?.[1]?.format('YYYY-MM-DD') : null,
                startDateExpire: values?.startToEndDateExpire?.[0] ? values?.startToEndDateExpire?.[0]?.format('YYYY-MM-DD') : null,
                endDateExpire: values?.startToEndDateExpire?.[1] ? values?.startToEndDateExpire?.[1]?.format('YYYY-MM-DD') : null
            })
            getReportHistory({
                ...values,
                startDate: values?.startToEndDate?.[0] ? values?.startToEndDate?.[0]?.format('YYYY-MM-DD') : null,
                endDate: values?.startToEndDate?.[1] ? values?.startToEndDate?.[1]?.format('YYYY-MM-DD') : null,
                startDateExpire: values?.startToEndDateExpire?.[0] ? values?.startToEndDateExpire?.[0]?.format('YYYY-MM-DD') : null,
                endDateExpire: values?.startToEndDateExpire?.[1] ? values?.startToEndDateExpire?.[1]?.format('YYYY-MM-DD') : null,
                ...pagination,
                year: values?.year?.format("YYYY"),
            })
        } catch (error) {
            console.log("error", error);

        }
    };

    return {
        handleSearch,
        exportEmployeeRegistrationReport,
        showReport,
        onChange,
        pagination,
        employeeTypeOptions,
        getReportHistory,
        getEmployeeTypeOptions,
        reportTableList,
        employeeStatusTypeOptions,
        informationTypeOptions,
        departmentOptions: modifiedDepartmentOption,
        positionOptions: modifiedPositionOptions,
        employmentTypeOptions,
        isModalOpen,
        setIsModalOpen,
        showModal,
        handleDateChange,
        isDownloading
    }
}
export default useEmployeeHistoryGroup;